import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getCoachee, getSession } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Edit, 
  Loader2, 
  Calendar, 
  Target,
  CheckSquare,
  TrendingUp,
  MessageCircle,
  Lightbulb,
  ClipboardCheck,
  Award,
  AlertCircle,
  HelpCircle,
  FileText,
  Hash,
  User
} from 'lucide-react';

export default function SessionDetail() {
  const { coacheeId, sessionId } = useParams();
  const navigate = useNavigate();
  const [coachee, setCoachee] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [coacheeId, sessionId]);

  const loadData = async () => {
    try {
      const [coacheeData, sessionData] = await Promise.all([
        getCoachee(coacheeId),
        getSession(coacheeId, sessionId)
      ]);
      setCoachee(coacheeData);
      setSession(sessionData);
    } catch (error) {
      toast.error('Error al cargar datos');
      navigate(`/coachees/${coacheeId}`);
    } finally {
      setLoading(false);
    }
  };

  const SectionCard = ({ icon: Icon, title, content, className = "" }) => (
    <Card className={`glass-card ${className}`}>
      <CardHeader className="border-b border-slate-100 py-4">
        <CardTitle className="text-heading text-base flex items-center gap-2">
          <Icon className="w-4 h-4 text-blue-900" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
          {content || <span className="text-slate-400 italic">No registrado</span>}
        </p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-blue-900" />
        </div>
      </Layout>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="session-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/coachees/${coacheeId}`)}
              className="p-2 hover:bg-slate-100 rounded-full mt-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-sky-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {session.numero_sesion}
                </div>
                <div>
                  <h1 className="text-heading text-3xl text-slate-800">
                    Sesión #{session.numero_sesion}
                  </h1>
                  <p className="text-slate-500 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {coachee?.nombre}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-slate-500 ml-[72px]">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {session.fecha}
                </span>
              </div>
            </div>
          </div>
          <Link to={`/coachees/${coacheeId}/sessions/${sessionId}/edit`}>
            <Button className="btn-primary" data-testid="edit-session-btn">
              <Edit className="w-4 h-4 mr-2" />
              Editar Sesión
            </Button>
          </Link>
        </div>

        {/* Objetivo de Proceso */}
        {session.objetivo_proceso && (
          <Card className="glass-card border-l-4 border-l-blue-900">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Target className="w-6 h-6 text-blue-900 flex-shrink-0 mt-1" />
                <div>
                  <p className="label-uppercase mb-2">Objetivo de Proceso</p>
                  <p className="text-slate-800 text-lg leading-relaxed">{session.objetivo_proceso}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Session Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SectionCard icon={CheckSquare} title="1. Bienvenida" content={session.bienvenida} />
          <SectionCard icon={ClipboardCheck} title="2. Revisión de Tareas" content={session.revision_tareas} />
          <SectionCard icon={TrendingUp} title="3. Avances en el Objetivo" content={session.avances_objetivo} />
          <SectionCard icon={FileText} title="4-5. Tema de la Sesión" content={session.tema_sesion} />
        </div>

        {/* Estado Actual y Deseado */}
        <Card className="glass-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-900" />
              6. Estado Actual y Deseado
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="label-uppercase mb-2">Estado Actual</p>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {session.estado_actual || <span className="text-slate-400 italic">No registrado</span>}
              </p>
            </div>
            <div>
              <p className="label-uppercase mb-2">Estado Deseado</p>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {session.estado_deseado || <span className="text-slate-400 italic">No registrado</span>}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* More Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SectionCard icon={Target} title="7. KPI de la Sesión" content={session.kpi_sesion} />
          <SectionCard icon={Lightbulb} title="8. Preguntas Significativas" content={session.preguntas_significativas} />
          <SectionCard icon={Award} title="9. Técnicas Utilizadas" content={session.tecnicas_utilizadas} />
          <SectionCard icon={ClipboardCheck} title="10. Tareas para Próxima Sesión" content={session.tareas_proxima_sesion} />
        </div>

        {/* Conclusiones */}
        <SectionCard 
          icon={MessageCircle} 
          title="11. Conclusiones y Aprendizajes" 
          content={session.conclusiones_aprendizajes}
          className="border-l-4 border-l-sky-500"
        />

        <SectionCard icon={CheckSquare} title="12. Revisión de KPI" content={session.revision_kpi} />

        {/* Coach Section */}
        <Card className="glass-card border-2 border-sky-200">
          <CardHeader className="border-b border-slate-100 bg-sky-50/50">
            <CardTitle className="text-heading text-lg flex items-center gap-2 text-sky-800">
              <Award className="w-5 h-5" />
              Para el Coach
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="label-uppercase mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Aprendizajes del Coach
                </p>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {session.aprendizajes_coach || <span className="text-slate-400 italic">No registrado</span>}
                </p>
              </div>
              <div>
                <p className="label-uppercase mb-2 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Fortaleza Desarrollada
                </p>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {session.fortaleza_desarrollada || <span className="text-slate-400 italic">No registrado</span>}
                </p>
              </div>
              <div>
                <p className="label-uppercase mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Área de Oportunidad
                </p>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {session.area_oportunidad || <span className="text-slate-400 italic">No registrado</span>}
                </p>
              </div>
              <div>
                <p className="label-uppercase mb-2 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  Preguntas al Meta Coach
                </p>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {session.preguntas_metacoach || <span className="text-slate-400 italic">No registrado</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="flex justify-center pb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/coachees/${coacheeId}`)}
            className="btn-outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Perfil
          </Button>
        </div>
      </div>
    </Layout>
  );
}
